import { supabase } from "./supabaseClient";

/**
 * Decide which site URL to use for auth redirects.
 * - In production, you want https://uniair.co.za
 * - In development, you want http://localhost:3000
 *
 * ✅ Set this in your env:
 *   CRA:   REACT_APP_SITE_URL=https://uniair.co.za
 *   Vite:  VITE_SITE_URL=https://uniair.co.za
 */
const getSiteUrl = () => {
  const envUrl =
    (typeof process !== "undefined" &&
      process.env &&
      (process.env.REACT_APP_SITE_URL || process.env.VITE_SITE_URL)) ||
    "";

  const fallback = typeof window !== "undefined" ? window.location.origin : "";
  const base = (envUrl || fallback || "").trim();
  return base.replace(/\/$/, "");
};

const isValidEmail = (email = "") =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

const getDomain = (email = "") => {
  const e = (email || "").trim().toLowerCase();
  const at = e.lastIndexOf("@");
  return at > -1 ? e.slice(at + 1) : "";
};

const normalizePhoneDigits = (raw = "") => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 10 && digits.startsWith("0")) {
    return `27${digits.slice(1)}`;
  }
  return digits;
};

const authService = {
  /* -------------------------------------------------------
     REGISTER
     - personal email is auth email (login + verification)
     - student_email is stored in metadata and synced to profiles
     - supports SA ID and Passport registrations
     - uses SITE_URL for redirect so it won't point to localhost in production
     - stores consent version metadata in auth.users raw_user_meta_data
     - NEW: university_id / campus_id / market_id metadata included
     - NEW: student email domain validation uses allowed_domains from UI
  -------------------------------------------------------- */
  register: async (userData) => {
    const SITE_URL = getSiteUrl();

    const personalEmail = (userData.email || "").toLowerCase().trim();
    const studentEmail = (userData.student_email || "").toLowerCase().trim();

    const identityType =
      (userData.identity_type || "").toLowerCase().trim() || null;
    const citizenshipCategory =
      (userData.citizenship_category || "").toUpperCase().trim() || null;

    const normalizedPhone = normalizePhoneDigits(userData.phone_number);

    const identityNumber =
      identityType === "sa_id"
        ? String(userData.identity_number || userData.id_number || "")
            .replace(/\D/g, "")
            .trim()
        : String(userData.identity_number || "")
            .replace(/[^A-Za-z0-9]/g, "")
            .toUpperCase()
            .trim();

    const legacyIdNumber =
      identityType === "sa_id"
        ? String(userData.id_number || userData.identity_number || "")
            .replace(/\D/g, "")
            .trim()
        : null;

    const universityId = (userData.university_id || "").trim();
    const campusId = (userData.campus_id || "").trim();
    const marketId = (userData.market_id || "").trim();

    const allowedDomains = Array.isArray(userData.allowed_domains)
      ? userData.allowed_domains
          .map((d) => String(d || "").toLowerCase().trim())
          .filter(Boolean)
      : [];

    if (!isValidEmail(personalEmail)) {
      throw new Error("Please enter a valid personal email address.");
    }

    if (!isValidEmail(studentEmail)) {
      throw new Error("Please enter a valid student email address.");
    }

    if (!universityId) {
      throw new Error("Please select your institution.");
    }

    if (!campusId) {
      throw new Error("Please select your campus.");
    }

    if (!marketId) {
      throw new Error("We could not determine your market. Please reselect your institution and campus.");
    }

    if (!allowedDomains.length) {
      throw new Error("No active student email domains were found for the selected institution.");
    }

    const studentDomain = getDomain(studentEmail);
    if (!allowedDomains.includes(studentDomain)) {
      throw new Error(
        `Please use your institution student email (${allowedDomains
          .map((d) => `@${d}`)
          .join(", ")}).`
      );
    }

    if (!identityType || !["sa_id", "passport"].includes(identityType)) {
      throw new Error("Please select a valid identity document type.");
    }

    if (identityType === "sa_id" && !/^[0-9]{13}$/.test(identityNumber)) {
      throw new Error("South African ID number must be exactly 13 digits.");
    }

    if (identityType === "passport" && !/^[A-Za-z0-9]{6,20}$/.test(identityNumber)) {
      throw new Error("Passport number must be 6 to 20 letters and/or numbers.");
    }

    if (!/^[1-9][0-9]{7,14}$/.test(normalizedPhone)) {
      throw new Error(
        "WhatsApp number must be a valid international number (include country code)."
      );
    }

    // -------------------------------------------------------
    // Uniqueness pre-check
    // -------------------------------------------------------
    let chk = null;

    try {
      const { data: chk2, error: err2 } = await supabase.rpc(
        "check_registration_uniqueness_v2",
        {
          p_student_number: userData.student_number,
          p_id_number: legacyIdNumber,
          p_student_email: studentEmail,
          p_phone_number: normalizedPhone,
          p_identity_type: identityType,
          p_identity_number: identityNumber,
        }
      );

      if (err2) throw err2;
      chk = chk2;
    } catch (e) {
      try {
        const { data: chk1, error: err1 } = await supabase.rpc(
          "check_registration_uniqueness",
          {
            p_student_number: userData.student_number,
            p_id_number: legacyIdNumber,
          }
        );

        if (err1) throw err1;
        chk = chk1;
      } catch (fallbackErr) {
        chk = { ok: true };
      }

      const { data: existingEmail, error: exEmailErr } = await supabase
        .from("profiles")
        .select("id")
        .ilike("student_email", studentEmail)
        .limit(1);

      if (!exEmailErr && existingEmail && existingEmail.length > 0) {
        chk = {
          ok: false,
          field: "student_email",
          reason: "That student email is already registered.",
        };
      }
    }

    if (!chk?.ok) {
      throw new Error(chk?.reason || "These details are already registered.");
    }

    // Best-effort duplicate checks for new identity model + phone
    const { data: existingPhone, error: phoneErr } = await supabase
      .from("profiles")
      .select("id")
      .eq("phone_number", normalizedPhone)
      .limit(1);

    if (!phoneErr && existingPhone && existingPhone.length > 0) {
      throw new Error("That WhatsApp number already exists.");
    }

    if (identityType === "passport") {
      const { data: existingPassport, error: passportErr } = await supabase
        .from("profiles")
        .select("id")
        .eq("identity_type", "passport")
        .eq("identity_number", identityNumber)
        .limit(1);

      if (!passportErr && existingPassport && existingPassport.length > 0) {
        throw new Error("That passport number is already registered.");
      }
    }

    if (identityType === "sa_id") {
      const { data: existingSAIdentity, error: saIdentityErr } = await supabase
        .from("profiles")
        .select("id")
        .eq("identity_type", "sa_id")
        .eq("identity_number", identityNumber)
        .limit(1);

      if (!saIdentityErr && existingSAIdentity && existingSAIdentity.length > 0) {
        throw new Error("That South African ID number is already registered.");
      }
    }

    // -------------------------------------------------------
    // Consent versions
    // -------------------------------------------------------
    const CONSENT_VERSION = "2026-02-13_v1";
    const isDriver = String(userData.user_type || "").toLowerCase() === "driver";
    if (isDriver && userData.has_own_vehicle !== true) {
      throw new Error("Driver applicants must confirm they own a vehicle before registering.");
    }

    // -------------------------------------------------------
    // Sign up with PERSONAL email
    // -------------------------------------------------------
    const { data, error } = await supabase.auth.signUp({
      email: personalEmail,
      password: userData.password,
      options: {
        emailRedirectTo: `${SITE_URL}/auth/callback`,
        data: {
          first_name: userData.first_name,
          last_name: userData.last_name,
          phone_number: normalizedPhone,
          student_number: userData.student_number,

          // Legacy SA-only field kept during transition
          id_number: legacyIdNumber,

          // New identity model
          identity_type: identityType,
          identity_number: identityNumber,
          citizenship_category:
            citizenshipCategory || (identityType === "sa_id" ? "ZA" : "INTL"),

          user_type: userData.user_type,
          // Self-declared eligibility only; actual ownership and documents are checked during driver approval.
          has_own_vehicle: isDriver ? true : null,

          // Eligibility email
          student_email: studentEmail,

          // NEW: market-scoping metadata
          university_id: universityId,
          campus_id: campusId,
          market_id: marketId,

          // Legal consent metadata
          tos_version: isDriver ? null : CONSENT_VERSION,
          driver_agreement_version: isDriver ? CONSENT_VERSION : null,
          consent_source: isDriver ? "driver-register" : "register",
        },
      },
    });

    if (error) throw error;

    return { success: true, data };
  },

  /* -------------------------------------------------------
     LOGIN
  -------------------------------------------------------- */
  login: async (email, password) => {
    const emailLower = (email || "").toLowerCase().trim();

    const { data, error } = await supabase.auth.signInWithPassword({
      email: emailLower,
      password,
    });

    if (error) throw error;

    const authUser = data?.user;

    if (authUser && !authUser.email_confirmed_at) {
      await supabase.auth.signOut({ scope: "local" });
      throw new Error("Please verify your email before logging in.");
    }

    let profile = null;

    try {
      profile = await authService.fetchCurrentUser();
    } catch (profileError) {
      // A temporary profile/database failure must not undo a successful login.
      console.warn("Signed in, but the UniAir profile could not be loaded:", profileError);

      return {
        user: {
          id: authUser?.id,
          email: authUser?.email,
          email_confirmed_at: authUser?.email_confirmed_at || null,
        },
        profileUnavailable: true,
      };
    }

    return { user: profile };
  },

  /* -------------------------------------------------------
     LOGOUT
  -------------------------------------------------------- */
  logout: async () => {
    // Log out only this browser/device. Other trusted driver devices stay active.
    await supabase.auth.signOut({ scope: "local" });
  },

  /* -------------------------------------------------------
     FETCH CURRENT USER (profiles + driver + vehicles)
  -------------------------------------------------------- */
  fetchCurrentUser: async () => {
    const {
      data: { session },
      error: sessionErr,
    } = await supabase.auth.getSession();

    if (sessionErr) throw sessionErr;
    if (!session?.user) return null;

    // Verify the auth token and fetch the protected profile in parallel.
    // Previously these two independent network round-trips ran serially on
    // every protected-page boot, delaying the first meaningful paint.
    let authUser = session.user;

    const userVerificationPromise = supabase.auth
      .getUser()
      .catch((userVerificationError) => ({
        data: { user: null },
        error: userVerificationError,
      }));

    const profilePromise = supabase
      .from("profiles")
      .select("*")
      .eq("id", session.user.id)
      .single();

    const sessionUserType = String(
      session.user?.user_metadata?.user_type || ""
    ).toLowerCase();

    // Driver identity is already present in signup metadata for normal driver
    // accounts. Prefetch the driver + vehicle record in the same network window
    // as the profile instead of waiting for the profile response first.
    const prefetchedDriverPromise =
      sessionUserType === "driver"
        ? supabase
            .from("drivers")
            .select(`
              *,
              vehicles (*)
            `)
            .eq("user_id", session.user.id)
            .maybeSingle()
        : Promise.resolve(null);

    const [userVerificationResult, profileResult, prefetchedDriverResult] =
      await Promise.all([
        userVerificationPromise,
        profilePromise,
        prefetchedDriverPromise,
      ]);

    const verifiedUser = userVerificationResult?.data?.user || null;
    const userErr = userVerificationResult?.error || null;

    if (userErr) {
      console.warn(
        "getUser verification failed; using stored session:",
        userErr?.message || userErr
      );
    } else if (verifiedUser) {
      authUser = verifiedUser;
    }

    const { data: profile, error: profileErr } = profileResult || {};
    if (profileErr) throw profileErr;

    let driverData = null;

    if ((profile?.user_type || "").toLowerCase() === "driver") {
      const driverResult = prefetchedDriverResult ||
        (await supabase
          .from("drivers")
          .select(`
            *,
            vehicles (*)
          `)
          .eq("user_id", authUser.id)
          .maybeSingle());

      const { data: driver, error: driverErr } = driverResult || {};

      if (driverErr) {
        console.warn("Driver fetch failed:", driverErr.message);
      } else if (driver) {
        if (Array.isArray(driver.vehicles)) {
          driver.vehicles = driver.vehicles[0] || null;
        }
        driverData = driver;
      }
    }

    return {
      id: authUser.id,
      email: authUser.email,
      email_confirmed_at: authUser.email_confirmed_at || null,
      ...profile,
      driver: driverData,
      needsProfile: !profile?.first_name,
    };
  },

  getCurrentUser: async () => {
    return await authService.fetchCurrentUser();
  },

  isAuthenticated: async () => {
    const { data } = await supabase.auth.getSession();
    return !!data.session;
  },

  getSiteUrl,
};

export default authService;